/**
 * Passkey-web-only sign-in errors. Isolated from password SignInApiError /
 * Contentful codes so SDK failures never surface as "Servers failed to connect".
 */

export interface PasskeyWebSignInError {
  errCode: string;
  msg: string;
}

export interface TransmitSdkError {
  errorCode?: string;
  message?: string;
  data?: unknown;
  name?: string;
}

/** Transmit ErrorCode values that mean the user dismissed the native dialog. */
const USER_DISMISSED_ERROR_CODES = [
  'webauthn_authentication_canceled',
  'authentication_aborted_timeout',
  'autofill_authentication_aborted',
];

const GENERIC_PASSKEY_MSG =
  'Passkey authentication failed. Please try another method.';

const AUTH_FAILED_MSG =
  'No passkey was found or authentication did not complete. Please sign in with your password.';

const SDK_NOT_READY_MSG =
  'Passkey sign-in is not ready yet. Please try again in a moment.';

const ALREADY_ACTIVE_MSG =
  'Passkey sign-in is already in progress. Please wait a moment and try again.';

const CROSS_DEVICE_MSG =
  'Could not complete passkey sign-in from another device. Please try again or use your password.';

const NOT_SUPPORTED_MSG =
  'Passkey sign-in is not available in this browser. Please sign in with your password.';

const NOT_ALLOWED_MSG =
  'Passkey sign-in was not permitted. Please try again or use your password.';

const CANCEL_MESSAGE_PATTERNS = [
  'cancel',
  'cancelled',
  'canceled',
  'abort',
  'aborted',
  'dismiss',
  'not allowed',
];

/** Transmit startAuthentication rejection — user is not registered. */
const START_AUTHENTICATION_FAILED_MSG = 'Failed to start authentication';

const getTransmitErrorCode = (err: unknown): string => {
  const sdk = (err ?? {}) as TransmitSdkError;
  return typeof sdk.errorCode === 'string' ? sdk.errorCode : '';
};

const getErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  }
  const sdk = (err ?? {}) as TransmitSdkError;
  return typeof sdk.message === 'string' ? sdk.message : String(err ?? '');
};

const getDomExceptionName = (err: unknown): string => {
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    return err.name;
  }
  const native = (err ?? {}) as { name?: unknown };
  return typeof native.name === 'string' ? native.name : '';
};

const looksLikeUserDismissal = (raw: string): boolean => {
  const lower = raw.toLowerCase();
  return CANCEL_MESSAGE_PATTERNS.some((p) => lower.includes(p));
};

export const isTransmitStartAuthenticationFailed = (err: unknown): boolean =>
  getTransmitErrorCode(err) === 'authentication_failed' &&
  getErrorMessage(err) === START_AUTHENTICATION_FAILED_MSG;

/** True when the error is the user closing/dismissing the native dialog. */
export const isPasskeyCancellation = (error: unknown): boolean => {
  const errorCode = getTransmitErrorCode(error);
  if (errorCode && USER_DISMISSED_ERROR_CODES.includes(errorCode)) {
    return true;
  }

  const name = getDomExceptionName(error);
  const message = getErrorMessage(error);
  // NotAllowedError is the browser's usual cancel signal when Transmit
  // does not wrap it with webauthn_authentication_canceled.
  if (name === 'NotAllowedError' && looksLikeUserDismissal(message)) {
    return true;
  }
  if (!errorCode && !name && looksLikeUserDismissal(message)) {
    return true;
  }
  return false;
};

export type PasskeyAttemptKind =
  | 'cancelled'
  | 'unavailable'
  | 'check_failed'
  | 'failed';

export type ClassifiedPasskeyWebError = {
  kind: PasskeyAttemptKind;
  error: PasskeyWebSignInError | null;
};

/**
 * Maps a Transmit SdkError (or browser DOMException) to a passkey-only
 * banner. Returns null for user cancellation so SignIn shows no error UI.
 */
export const parsePasskeyWebSignInError = (
  err: unknown
): PasskeyWebSignInError | null => {
  const errorCode = getTransmitErrorCode(err);
  const message = getErrorMessage(err);
  const name = getDomExceptionName(err);

  if (isPasskeyCancellation(err)) {
    return null;
  }

  console.error('[Passkey] Web SDK error:', {
    errorCode: errorCode || undefined,
    name: name || undefined,
    message,
  });

  switch (errorCode) {
    case 'not_initialized':
      return { errCode: 'NOT_INITIALIZED', msg: SDK_NOT_READY_MSG };
    case 'authentication_failed':
    case 'unknown':
      return { errCode: 'AUTHENTICATION_FAILED', msg: AUTH_FAILED_MSG };
    case 'authentication_process_already_active':
      return { errCode: 'ALREADY_ACTIVE', msg: ALREADY_ACTIVE_MSG };
    case 'cross_device_init_failed':
    case 'cross_device_status_failed':
      return { errCode: 'CROSS_DEVICE_FAILED', msg: CROSS_DEVICE_MSG };
    default:
      break;
  }

  if (name === 'NotSupportedError' || name === 'SecurityError') {
    return { errCode: 'NOT_SUPPORTED', msg: NOT_SUPPORTED_MSG };
  }

  if (name === 'NotAllowedError') {
    return { errCode: 'NOT_ALLOWED', msg: NOT_ALLOWED_MSG };
  }

  return {
    errCode: 'PASSKEY_SDK_ERROR',
    msg: GENERIC_PASSKEY_MSG,
  };
};

/**
 * Classifies a Transmit/browser error for silent evaluation fallback.
 * Cancel and "no passkey" fall through to password without a banner.
 */
export const classifyPasskeyWebSignInError = (
  err: unknown
): ClassifiedPasskeyWebError => {
  const parsed = parsePasskeyWebSignInError(err);
  if (!parsed) {
    return { kind: 'cancelled', error: null };
  }
  if (parsed.errCode === 'NOT_INITIALIZED') {
    return { kind: 'check_failed', error: parsed };
  }
  if (parsed.errCode === 'AUTHENTICATION_FAILED') {
    return { kind: 'unavailable', error: parsed };
  }
  return { kind: 'failed', error: parsed };
};

export const passkeyMiddlewareError = (): PasskeyWebSignInError => ({
  errCode: 'PASSKEY_MW_ERROR',
  msg: GENERIC_PASSKEY_MSG,
});
